import { useState } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { Formik, FieldArray, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import moment from 'moment';
import Flatpickr from 'react-flatpickr';
import Select from 'react-select';
import { useSelector } from 'react-redux';
import {
  shiftTimeSlots,
  tConvert,
  convertTimeToMinutes,
  isTimeInShiftRange,
  toLocalDateString,
  getUserData,
} from '../../helpers';
import Avatar from '../Avatar';
import TimeZoneDropdown from '../common/TimeZoneDropdown';

const EditSchedule = ({
  scheduleInfo,
  showEditSchedule,
  handleEditScheduleDisplay,
  exitingShifts,
  handleUpdateSchedule,
  breaksList,
}) => {
  const permissions = useSelector((state) => state.permissionsReducer);
  const datePickerOptions = {
    allowInvalidPreload: true,
    // minDate: 'today',
    dateFormat: 'M j, Y',
    monthSelectorType: 'static',
  };

  const shitOptions =
    exitingShifts && exitingShifts.length > 0
      ? exitingShifts.map((eShift) => {
          const sOption = {
            value: eShift.shiftId,
            label: eShift.name,
            shiftStart: eShift.shiftStart,
            shiftEnd: eShift.shiftEnd,
          };
          return sOption;
        })
      : [];
  const timeZone = getUserData('timeZone') || '';
  const shiftTypes = [...shitOptions];
  const timeSlots = shiftTimeSlots();
  const [fromTime, setFromTime] = useState(
    scheduleInfo?.extendedProps
      ? {
          label: moment
            .utc(scheduleInfo.extendedProps.shiftStart)
            .format('hh:mm A'),
          value: moment
            .utc(scheduleInfo.extendedProps.shiftStart)
            .format('HH:mm'),
        }
      : ''
  );
  const [toTime, setToTime] = useState(
    scheduleInfo?.extendedProps
      ? {
          label: moment
            .utc(scheduleInfo.extendedProps.shiftEnd)
            .format('hh:mm A'),
          value: moment
            .utc(scheduleInfo.extendedProps.shiftEnd)
            .format('HH:mm'),
        }
      : ''
  );

  const breakOptions =
    breaksList && breaksList.length > 0
      ? breaksList.map((breakItem) => {
          const bOption = {
            value: breakItem._id,
            label: `${breakItem.name} ${
              breakItem.shortCode ? `(${breakItem.shortCode})` : ''
            }`,
          };
          return bOption;
        })
      : [];

  const scheduleValidationSchema = (shiftFrom, shiftTo) =>
    Yup.object({
      shiftId: Yup.string().nullable(true),
      shiftFrom: Yup.string().required('Please select start time'),
      shiftTo: Yup.string().required('Please select end time'),
      shiftDate: Yup.date().required('Please select shift date'),
      breaks: Yup.array().of(
        Yup.object().shape({
          breakType: Yup.object().required('Break end time is required'),
          breakStart: Yup.object()
            .required('Break start time is required')
            .test(
              'is-within-shift',
              'Break start time must be within shift hours',
              function (breakStart) {
                if (!breakStart || !breakStart.value) return true;

                const startTime =
                  shiftFrom ||
                  moment
                    .utc(scheduleInfo.extendedProps.shiftStart)
                    .format('HH:mm');
                const endTime =
                  shiftTo ||
                  moment
                    .utc(scheduleInfo.extendedProps.shiftEnd)
                    .format('HH:mm');

                const shiftStartMinutes = convertTimeToMinutes(startTime);
                const shiftEndMinutes = convertTimeToMinutes(endTime);
                const breakStartMinutes = convertTimeToMinutes(breakStart);

                return isTimeInShiftRange(
                  breakStartMinutes,
                  shiftStartMinutes,
                  shiftEndMinutes
                );
              }
            ),
          breakEnd: Yup.object()
            .required('Break end time is required')
            .test(
              'is-within-shift',
              'Break end time must be within shift hours',
              function (breakEnd) {
                if (!breakEnd || !breakEnd.value) return true;

                const startTime =
                  shiftFrom ||
                  moment
                    .utc(scheduleInfo.extendedProps.shiftStart)
                    .format('HH:mm');
                const endTime =
                  shiftTo ||
                  moment
                    .utc(scheduleInfo.extendedProps.shiftEnd)
                    .format('HH:mm');

                const shiftStartMinutes = convertTimeToMinutes(startTime);
                const shiftEndMinutes = convertTimeToMinutes(endTime);
                const breakEndMinutes = convertTimeToMinutes(breakEnd);

                return isTimeInShiftRange(
                  breakEndMinutes,
                  shiftStartMinutes,
                  shiftEndMinutes
                );
              }
            )
            .test(
              'end-after-start',
              'Break end time must be after break start time',
              function (breakEnd) {
                // eslint-disable-next-line react/no-this-in-sfc
                const { breakStart } = this.parent;

                if (!breakStart?.value || !breakEnd?.value) return true;

                const start = convertTimeToMinutes(breakStart);
                let end = convertTimeToMinutes(breakEnd);

                if (end < start) {
                  end += 1440; // assume break ends next day
                }

                return end > start;
              }
            ),
        })
      ),
    });

  return (
    <>
      <Modal
        show={showEditSchedule}
        onHide={() => {
          handleEditScheduleDisplay(false);
        }}
        backdrop={false}
        centered
      >
        <Modal.Header>
          <Modal.Title className="m-0">Edit Schedule</Modal.Title>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => {
              handleEditScheduleDisplay(false);
            }}
          />
        </Modal.Header>
        {scheduleInfo && scheduleInfo.extendedProps && (
          <Formik
            initialValues={{
              scheduleId: scheduleInfo.id,
              teamMemberId: scheduleInfo.extendedProps.userId,
              scheduleType: scheduleInfo.extendedProps.scheduleType,
              shiftId: scheduleInfo.extendedProps.shiftId
                ? scheduleInfo.extendedProps.shiftId
                : null,
              shiftFrom: moment
                .utc(scheduleInfo.extendedProps.shiftStart)
                .format('HH:mm'),
              shiftTo: moment
                .utc(scheduleInfo.extendedProps.shiftEnd)
                .format('HH:mm'),
              shiftDate: moment
                .utc(scheduleInfo.extendedProps.shiftStart)
                .toLocaleString(),
              timezone: timeZone,
              breaks:
                scheduleInfo.extendedProps.breaks?.length > 0
                  ? scheduleInfo.extendedProps.breaks.map((breakItem) => ({
                      breakType: {
                        label: breakItem.name,
                        value: breakItem.breakTypeId,
                        shortCode: breakItem.shortCode,
                      },
                      breakStart: {
                        label: moment
                          .utc(breakItem.breakStart)
                          .format('hh:mm A'),
                        value: moment.utc(breakItem.breakStart).format('HH:mm'),
                      },
                      breakEnd: {
                        label: moment.utc(breakItem.breakEnd).format('hh:mm A'),
                        value: moment.utc(breakItem.breakEnd).format('HH:mm'),
                      },
                    }))
                  : [],
            }}
            validationSchema={scheduleValidationSchema(fromTime, toTime)}
            onSubmit={(values, { resetForm }) => {
              resetForm();
            }}
          >
            {(formikprops) => (
              <Form onSubmit={formikprops.handleSubmit}>
                <Modal.Body>
                  <section>
                    <Form.Group>
                      <Row className="align-items-center">
                        <Col className="col-auto">
                          <div className="avatar avatar-md">
                            <Avatar
                              firstName={scheduleInfo.extendedProps.firstName}
                              lastName={scheduleInfo.extendedProps.lastName}
                              defaultBg={scheduleInfo.extendedProps.bgColor}
                              imgPath={scheduleInfo.extendedProps.avatar}
                            />
                          </div>
                        </Col>
                        <Col className="ps-0">
                          <h4 className="mb-0">
                            {scheduleInfo.extendedProps.userRealName}
                          </h4>
                        </Col>
                      </Row>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Shift Slot</Form.Label>
                      <Select
                        options={shiftTypes}
                        className="basic-multi-select"
                        placeholder="Select shift"
                        name="shiftId"
                        defaultValue={
                          scheduleInfo.extendedProps.shiftId
                            ? shiftTypes.find(
                                (option) =>
                                  option.value ===
                                  scheduleInfo.extendedProps.shiftId
                              )
                            : null
                        }
                        onChange={(event) => {
                          formikprops.setFieldValue('shiftId', event.value);
                          formikprops.setFieldValue(
                            'shiftFrom',
                            event.shiftStart
                          );
                          formikprops.setFieldValue('shiftTo', event.shiftEnd);
                          setFromTime({
                            value: event.shiftStart,
                            label: tConvert(event.shiftStart),
                          });
                          setToTime({
                            value: event.shiftEnd,
                            label: tConvert(event.shiftEnd),
                          });
                        }}
                        theme={(theme) => ({
                          ...theme,
                          colors: {
                            ...theme.colors,
                            primary: '#0649b5',
                            neutral10: '#edf2f9',
                            neutral20: '#d2ddec',
                            neutral30: '#d2ddec',
                            neutral50: '#b1c2da',
                            neutral80: '#12263f',
                            dangerLight: '#DEEBFF',
                            danger: '#12263f',
                          },
                        })}
                      />
                      <ErrorMessage
                        name="shiftId"
                        render={(msg) => (
                          <small className="text-danger">{msg}</small>
                        )}
                      />
                    </Form.Group>

                    <Row>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Shift Duration From</Form.Label>
                          <div>{tConvert(formikprops.values.shiftFrom)}</div>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Shift Duration To</Form.Label>
                          <div>{tConvert(formikprops.values.shiftTo)}</div>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Form.Group controlId="Timezone">
                        <Form.Label>Timezone</Form.Label>
                        <TimeZoneDropdown
                          timeZoneName="timezone"
                          formik={formikprops}
                          defaultValue={timeZone}
                        />
                      </Form.Group>
                      <Col>
                        <Form.Group controlId="shiftDate">
                          <Form.Label>Shift Date</Form.Label>
                          <Flatpickr
                            onChange={([date]) => {
                              const normalizedDate = toLocalDateString(date);
                              formikprops.setFieldValue(
                                'shiftDate',
                                normalizedDate
                              );
                            }}
                            placeholder=""
                            options={{
                              ...datePickerOptions,
                              defaultDate: moment
                                .utc(scheduleInfo.extendedProps.shiftStart)
                                .toLocaleString(),
                            }}
                            className="form-control icon-calendar due-date"
                            name="shiftDate"
                          />
                          <ErrorMessage
                            name="shiftDate"
                            render={(msg) => (
                              <small className="text-danger">{msg}</small>
                            )}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </section>
                  {breaksList?.length > 0 && (
                    <FieldArray
                      name="breaks"
                      render={(arrayHelpers) => (
                        <div>
                          {formikprops.values.breaks &&
                            formikprops.values.breaks.length > 0 &&
                            formikprops.values.breaks.map((shift, index) => {
                              if (shift.status !== 'delete') {
                                return (
                                  <Row key={index}>
                                    <Col xs={5}>
                                      <Form.Group>
                                        <Select
                                          options={breakOptions}
                                          className="basic-multi-select"
                                          placeholder="Select Break Type"
                                          name={`breaks.${index}.breakType`}
                                          value={
                                            formikprops.values.breaks[index]
                                              .breakType.value
                                              ? {
                                                  label: `${
                                                    formikprops.values.breaks[
                                                      index
                                                    ].breakType.label
                                                  } ${
                                                    formikprops.values.breaks[
                                                      index
                                                    ].breakType.shortCode
                                                      ? `(${formikprops.values.breaks[index].breakType.shortCode})`
                                                      : ''
                                                  }`,
                                                  value:
                                                    formikprops.values.breaks[
                                                      index
                                                    ].breakType.value,
                                                }
                                              : null
                                          }
                                          onChange={(event) =>
                                            formikprops.setFieldValue(
                                              `breaks.${index}.breakType`,
                                              event
                                            )
                                          }
                                          theme={(theme) => ({
                                            ...theme,
                                            colors: {
                                              ...theme.colors,
                                              primary: '#0649b5',
                                              neutral10: '#edf2f9',
                                              neutral20: '#d2ddec',
                                              neutral30: '#d2ddec',
                                              neutral50: '#b1c2da',
                                              neutral80: '#12263f',
                                              dangerLight: '#DEEBFF',
                                              danger: '#12263f',
                                            },
                                          })}
                                        />
                                        <ErrorMessage
                                          name={`breaks.${index}.breakType`}
                                          render={(msg) => (
                                            <small className="text-danger">
                                              {msg}
                                            </small>
                                          )}
                                        />
                                      </Form.Group>
                                    </Col>
                                    <Col xs={3}>
                                      <Select
                                        name={`breaks.${index}.breakStart`}
                                        options={timeSlots}
                                        className="basic-multi-select"
                                        placeholder="From"
                                        value={
                                          formikprops.values.breaks[index]
                                            .breakStart.value
                                            ? {
                                                label:
                                                  formikprops.values.breaks[
                                                    index
                                                  ].breakStart.label,
                                                value:
                                                  formikprops.values.breaks[
                                                    index
                                                  ].breakStart.value,
                                              }
                                            : null
                                        }
                                        onChange={(event) =>
                                          formikprops.setFieldValue(
                                            `breaks.${index}.breakStart`,
                                            event
                                          )
                                        }
                                        theme={(theme) => ({
                                          ...theme,
                                          colors: {
                                            ...theme.colors,
                                            primary: '#0649b5',
                                            neutral10: '#edf2f9',
                                            neutral20: '#d2ddec',
                                            neutral30: '#d2ddec',
                                            neutral50: '#b1c2da',
                                            neutral80: '#12263f',
                                            dangerLight: '#DEEBFF',
                                            danger: '#12263f',
                                          },
                                        })}
                                      />
                                      <ErrorMessage
                                        name={`breaks.${index}.breakStart`}
                                        render={(msg) => (
                                          <small className="text-danger">
                                            {msg}
                                          </small>
                                        )}
                                      />
                                    </Col>
                                    <Col xs={3}>
                                      <Select
                                        name={`breaks.${index}.breakEnd`}
                                        options={timeSlots}
                                        className="basic-multi-select"
                                        placeholder="To"
                                        value={
                                          formikprops.values.breaks[index]
                                            .breakEnd.value
                                            ? {
                                                label:
                                                  formikprops.values.breaks[
                                                    index
                                                  ].breakEnd.label,
                                                value:
                                                  formikprops.values.breaks[
                                                    index
                                                  ].breakEnd.value,
                                              }
                                            : null
                                        }
                                        onChange={(event) =>
                                          formikprops.setFieldValue(
                                            `breaks.${index}.breakEnd`,
                                            event
                                          )
                                        }
                                        theme={(theme) => ({
                                          ...theme,
                                          colors: {
                                            ...theme.colors,
                                            primary: '#0649b5',
                                            neutral10: '#edf2f9',
                                            neutral20: '#d2ddec',
                                            neutral30: '#d2ddec',
                                            neutral50: '#b1c2da',
                                            neutral80: '#12263f',
                                            dangerLight: '#DEEBFF',
                                            danger: '#12263f',
                                          },
                                        })}
                                      />
                                      <ErrorMessage
                                        name={`breaks.${index}.breakEnd`}
                                        render={(msg) => (
                                          <small className="text-danger">
                                            {msg}
                                          </small>
                                        )}
                                      />
                                    </Col>
                                    <Col xs={1}>
                                      <Button
                                        variant="link"
                                        onClick={() =>
                                          arrayHelpers.remove(index)
                                        }
                                      >
                                        <i className="mdi mdi-window-close" />
                                      </Button>
                                    </Col>
                                  </Row>
                                );
                              }
                              return null;
                            })}
                          <div className="mb-4">
                            <Button
                              variant="outline-primary"
                              type="button"
                              size="sm"
                              onClick={() =>
                                arrayHelpers.push({
                                  breakType: '',
                                  breakStart: '',
                                  breakEnd: '',
                                })
                              }
                            >
                              Add Break
                            </Button>
                          </div>
                        </div>
                      )}
                    />
                  )}
                  {formikprops.values.scheduleType === 'overtime' && (
                    <Alert variant="light" className="mb-0">
                      <strong>Note:</strong> OT shifts require approval. They’ll
                      be published automatically once accepted by the assigned
                      user.
                    </Alert>
                  )}
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between">
                  <Button
                    variant="link"
                    onClick={() => {
                      handleEditScheduleDisplay(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <span>
                    {scheduleInfo.extendedProps.isPublished &&
                    permissions &&
                    permissions.includes('schedule_publish') ? (
                      <Button
                        variant="primary"
                        type="submit"
                        onClick={() => {
                          if (formikprops.isValid) {
                            handleUpdateSchedule({
                              ...formikprops.values,
                              isPublished: true,
                            });
                          }
                        }}
                      >
                        Update & Publish
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="outline-primary"
                          type="submit"
                          className="me-2"
                          onClick={() => {
                            if (formikprops.isValid) {
                              handleUpdateSchedule({
                                ...formikprops.values,
                                isPublished: false,
                              });
                            }
                          }}
                        >
                          Update Draft
                        </Button>
                        {permissions &&
                          permissions.includes('schedule_publish') && (
                            <Button
                              variant="primary"
                              type="submit"
                              onClick={() => {
                                handleUpdateSchedule({
                                  ...formikprops.values,
                                  isPublished: true,
                                });
                              }}
                              disabled={
                                formikprops.values.scheduleType === 'overtime'
                              }
                            >
                              Update & Publish
                            </Button>
                          )}
                      </>
                    )}
                  </span>
                </Modal.Footer>
              </Form>
            )}
          </Formik>
        )}
      </Modal>
    </>
  );
};

EditSchedule.propTypes = {
  scheduleInfo: PropTypes.object,
  showEditSchedule: PropTypes.bool,
  handleEditScheduleDisplay: PropTypes.func,
  exitingShifts: PropTypes.array,
  handleUpdateSchedule: PropTypes.func,
  breaksList: PropTypes.array,
};

export default EditSchedule;
